import { Vue, Options } from 'vue-class-component';
import {
  PlantDTO,
  PlantIconEnum,
  PlantTagDTO,
  PlantType,
} from '@/../client-generator/generated-clients/plants';
import debounce from 'lodash.debounce';
import plantsClient from '@/core/services/plants';
import {
  getPlantIconImages,
  getPlantIconImageUrls,
  NAME_VALIDATION_DEBOUNCE_TIME,
} from '@/core/utils/utils';
import {
  Country,
  PlantEntityType,
} from '@/../client-generator/generated-clients/configurations';

import { SET_APP_LOADING } from '@/core/store/action-types';
import { NamingService } from '@/core/services/naming.service';
import configurationsClient from '@/core/services/configurations-service';
import { mapGetters } from 'vuex';

const kebabCase = require('lodash.kebabcase');

// get the max scale to fit
function drawScaledImage(ctx: any, img: any) {
  var scale = Math.max(
    ctx.canvas.width / img.width,
    ctx.canvas.height / img.height
  );
  var x = (ctx.canvas.width - img.width * scale) / 2;
  var y = (ctx.canvas.height - img.height * scale) / 2;
  ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
}

@Options({
  name: 'DialogPlantCreateStep1',
  props: {
    plant: {
      required: true,
      type: Object as () => PlantDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },
    preSelectedTags: {
      type: Object as () => PlantTagDTO[],
    },
  },
  watch: {
    'plant.name': function () {
      this.verifyValidityOfName();
    },
    'plant.address': function () {
      this.checkNextButtonState();
    }
  },
  emits: ['is-loading', 'next-enabled'],
  computed: {
    ...mapGetters('UI', ['plantEditImageCustomEnabled']),
  },
})
export default class DialogPlantCreateStep1 extends Vue {
  plant!: PlantDTO;
  initialName!: string;
  preSelectedTags!: PlantTagDTO[] | undefined;
  plantEditImageCustomEnabled!: boolean;

  plantId: number | null = null;

  tempTags: PlantTagDTO[] = [];
  countries: Country[] = [];
  newTagName = '';

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  get title() {
    return this.$t(
      !this.plantId ? 'AppResource.CreateThePlant' : 'AppResource.PlantInfo'
    ).toString();
  }

  get isCreateTagDisabled() {
    return !this.newTagName.trim();
  }

  get images() {
    const images = [
      ...getPlantIconImages().map((image) => ({
        imageURLs: getPlantIconImageUrls(image),
        value: image.value,
      })),
    ];
    if (this.plantEditImageCustomEnabled)
      images.push({
        imageURLs: [
          this.plant.customIconDataUrl ||
            (this.plant as any).customIconImageUrl ||
            require(`@/assets/images/plants/add-your-image-here.jpg`),
        ],
        value: PlantIconEnum.ICON_CUSTOM,
      });
    return images;
  }

  created() {
    if (this.plant.tags && this.plant.tags.length > 0) {
      this.tempTags = this.plant.tags;
    }

    if (
      !this.tempTags ||
      this.tempTags.length === 0 ||
      this.countries.length === 0
    ) {
      this.$emit('is-loading', true);
      Promise.all([this.fetchPlantTags(), this.fetchCountries()]).finally(
        () => {
          this.$emit('is-loading', false);
        }
      );
    }
  }

  async beforeMount() {
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME
    );
    this.checkNextButtonState();

    NamingService.flushCache();
    await NamingService.prefetchFlatEntities(
      0, // this must be 0, as from API specification
      PlantEntityType.PLANT
    );
  }

  beforeUnmount() {
    document.querySelector('#uploadCustomImageInput')?.remove();
    document
      .querySelector(
        `.el-scrollbar__view .p-button[aria-label="${PlantIconEnum.ICON_CUSTOM}"]`
      )
      ?.removeEventListener('click', this.handlerUploadInput);
  }

  async mounted() {
    document
      .querySelector(
        `.el-scrollbar__view .p-button[aria-label="${PlantIconEnum.ICON_CUSTOM}"]`
      )
      ?.addEventListener('click', this.handlerUploadInput);
  }

  updated() {
    if (this.plantId && this.plant?.name) this.initialName = this.plant.name;
  }

  fileRead(imageBinary: string | undefined) {
    if (!imageBinary) {
      this.plant.icon = undefined;
    }
    this.plant.customIconDataUrl = imageBinary;
  }

  async fetchPlantTags() {
    try {
      this.tempTags = await plantsClient.tagsList();
    } catch (error) {
      console.log(error);
    }
  }

  async fetchCountries() {
    try {
      this.countries = await configurationsClient.countriesAutocomplete(PlantType.AUTOMATION_PLANT);
    } catch (error) {
      console.log(error);
    }
  }

  handlerUploadInput(e: any) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    let uploadInput: any = document.querySelector('#uploadCustomImageInput');
    if (!uploadInput) {
      // upload input create
      uploadInput = document.createElement('input');
      uploadInput.id = 'uploadCustomImageInput';
      uploadInput.setAttribute('type', 'file');
      uploadInput.setAttribute('hidden', '');
      uploadInput.setAttribute('accept', '.bmp,.gif,.jpeg,.jpg,.png,.tiff');
      document.body.append(uploadInput);

      // upload input change listener
      uploadInput.addEventListener('change', (e: any) => {
        const reader = new FileReader();
        reader.onload = (evLoad) => {
          // reader loaded data url
          let image = new Image();
          image.onload = () => {
            // image loaded for size reason)
            let canvas = document.createElement('CANVAS') as any;
            canvas.width = 680;
            canvas.height = 320;
            let ctx = canvas.getContext('2d');
            // use canvas and image for scaling the size
            drawScaledImage(ctx, image);
            this.fileRead(canvas.toDataURL());
          };
          image.src = evLoad.target?.result as string;
        };
        reader.onerror = (err) => {
          console.error(err);
        };

        if (e.target.files.length > 0) {
          reader.readAsDataURL(uploadInput.files[0]);
        } else {
          this.fileRead(undefined);
        }
      });
    }
    uploadInput.click();
  }

  addTag() {
    this.newTagName = this.newTagName.trim();

    if (!this.newTagName) return;

    const tagKey = kebabCase(this.newTagName);

    if (!this.plant.tags) {
      console.log('plant.tags undefined');
      return;
    }
    if (this.plant.tags.some((t) => t.key == tagKey)) {
      return;
    }
    const newTag = new PlantTagDTO({
      key: tagKey,
      value: this.newTagName,
    });
    this.tempTags.push(newTag);
    this.plant.tags.push(newTag);

    this.newTagName = '';
  }

  toggleTagSelection(tag: PlantTagDTO) {
    if (!this.plant.tags) {
      console.log('plant.tags undefined');
      return;
    }

    const groupIndex = this.plant.tags.findIndex((g) => g.key === tag.key);

    if (groupIndex >= 0) {
      this.plant.tags.splice(groupIndex, 1);
    } else {
      this.plant.tags.push(tag);
    }
  }

  isTagSelected(tag: PlantTagDTO) {
    return this.plant.tags?.some((x) => x.key === tag.key);
  }

  private async verifyValidityOfName() {
    this.$store.dispatch(SET_APP_LOADING, true);
    const validName = NamingService.isValidEntityTypeName(this.plant.name);
    if (this.plant.name && this.initialName !== this.plant.name && !validName) {
      const suggestedName = await NamingService.suggestNextValidEntityName({
        plantId: this.plantId!,
        eType: PlantEntityType.PLANT,
        metadata: { plantSourceName: this.plant.name },
      });
      this.isValidName = validName;
      this.invalidNameReason = this.$t(
        `uniqueNaming.validationErrorWithSuggestion`,
        { suggestedName }
      );
      this.$emit('next-enabled', false);
    } else {
      this.isValidName = true;
      this.invalidNameReason = '';
      this.$emit('next-enabled', true);
    }
    this.checkNextButtonState();
    this.$store.dispatch(SET_APP_LOADING, false);
  }

  private checkNextButtonState() {
    if (this.plant.name && this.isValidName && this.plant.address)
      this.$emit('next-enabled', true);
    else this.$emit('next-enabled', false);
  }
}
